/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { formatPrice } from '../../../../util/helpers';
import { CostPrice } from '../../costprice.types';
import CostPriceEmpty from './CostPriceEmpty';
import TestIds from '../../../../testIds';

export type TProcessedCostPrices = [string, CostPrice[]];
export interface ICostPriceSuccessProps {
  processedCostPrices: TProcessedCostPrices[];
}

const CostPriceSuccess: FC<ICostPriceSuccessProps> = ({
  processedCostPrices,
}: ICostPriceSuccessProps): JSX.Element => {
  const getLabel = (breakpoint: CostPrice) => {
    if (breakpoint.quantityTo === -1) {
      return 'All units';
    }

    const toLabel = breakpoint.quantityTo === 0 ? 'any' : breakpoint.quantityTo;
    return `${breakpoint.quantityFrom}-${toLabel} ${breakpoint.unit}`;
  };

  if (!processedCostPrices.length) {
    return <CostPriceEmpty />;
  }

  return (
    <Stack spacing='0.5rem' data-testid={TestIds.feat.costPrice.success.component}>
      {processedCostPrices.map(([site, siteBreaks]: TProcessedCostPrices) => (
        <Box key={site} data-testid={TestIds.feat.costPrice.success.siteContainer}>
          <Typography variant='defaultImportant'>{site}</Typography>

          {siteBreaks.map((breakpoint: CostPrice, index: number) => (
            <Stack
              key={`${index}-${breakpoint.price}`}
              spacing={2}
              direction='row'
              data-testid={TestIds.feat.costPrice.success.siteBreakpointPrice}
            >
              <Typography variant='default' color='subtle'>
                {getLabel(breakpoint)}
              </Typography>

              <Typography variant='default' color='greyed'>
                {formatPrice(breakpoint.price)}
              </Typography>
            </Stack>
          ))}
        </Box>
      ))}
    </Stack>
  );
};

export default CostPriceSuccess;
